import { useEffect, useRef, useState } from 'react';
import './App.css';
import { api } from './lib/api';
import { trackPageView } from './lib/analytics';
import { navigate, useRoute } from './lib/router';
import DashboardLayout from './layouts/DashboardLayout';
import { LoginPage } from './pages/auth/AuthPages';
import { ClaimPage, VoucherPage, NotFoundPage } from './pages/customer/CustomerPages';
import {
  DashboardPage,
  AnalyticsPage,
  MerchantRelationsPage,
  StaffAccessPage,
  PlanBillingPage,
  BusinessSettingsPage,
  ContactPage as MerchantContactPage,
  VoucherSettingsPage,
  CustomersPage,
  RedeemPage,
  QRPage,
} from './pages/dashboard/DashboardPages';
import LandingPage from './pages/home/LandingPage';
import PublicContactPage from './pages/home/ContactPage';
import {
  ResourceArticlePage,
  ResourcesPage,
  isResourceArticle,
} from './pages/home/ResourcesPages';
import { Spinner } from './components/UI';

const merchant = {
  '/merchant': DashboardPage,
  '/merchant/analytics': AnalyticsPage,
  '/merchant/relations': MerchantRelationsPage,
  '/merchant/staff': StaffAccessPage,
  '/merchant/billing': PlanBillingPage,
  '/merchant/voucher': VoucherSettingsPage,
  '/merchant/customers': CustomersPage,
  '/merchant/redeem': RedeemPage,
  '/merchant/qr': QRPage,
  '/merchant/settings': BusinessSettingsPage,
  '/merchant/contact': MerchantContactPage,
};

export default function App() {
  const path = useRoute();
  const lastTrackedPath = useRef(null);
  const [session, setSession] = useState({ loading: true, user: null });

  useEffect(() => {
    if (lastTrackedPath.current === path) return;
    lastTrackedPath.current = path;
    trackPageView(path);
  }, [path]);

  useEffect(() => {
    let live = true;
    api
      .me()
      .then((user) => live && setSession({ loading: false, user }))
      .catch(() => live && setSession({ loading: false, user: null }));
    return () => {
      live = false;
    };
  }, []);

  if (path === '/' || path === '/landing' || path === '/landing/') {
    return <LandingPage />;
  }
  if (path.startsWith('/landing/') && path.length > '/landing/'.length) {
    try {
      return <LandingPage trackingSource={decodeURIComponent(path.slice('/landing/'.length))} />;
    } catch {
      return <NotFoundPage />;
    }
  }
  if (path === '/contact' || path === '/contact/') {
    return <PublicContactPage />;
  }
  if (path === '/resources' || path === '/resources/') {
    return <ResourcesPage />;
  }
  if (path.startsWith('/resources/')) {
    const slug = path.slice('/resources/'.length).replace(/\/$/, '');
    if (isResourceArticle(slug)) return <ResourceArticlePage slug={slug} />;
    return <NotFoundPage />;
  }
  if (path === '/merchant/login') {
    return <LoginPage onLogin={(user) => setSession({ loading: false, user })} />;
  }
  if (path.startsWith('/c/')) {
    return <ClaimPage slug={decodeURIComponent(path.slice(3))} />;
  }
  if (path.startsWith('/v/')) {
    return <VoucherPage token={decodeURIComponent(path.slice(3))} />;
  }
  const Page = merchant[path];
  if (Page) {
    if (session.loading) {
      return (
        <main className="center-state">
          <Spinner label="Restoring your session" />
        </main>
      );
    }
    if (!session.user) {
      navigate(`/merchant/login?next=${encodeURIComponent(path)}`, { replace: true });
      return null;
    }
    return (
      <DashboardLayout
        user={session.user}
        path={path}
        onLogout={() => setSession({ loading: false, user: null })}
      >
        <Page
          user={session.user}
          onUserChange={(user) => setSession({ loading: false, user })}
        />
      </DashboardLayout>
    );
  }

  return <NotFoundPage />;
}
